import { IconBulbFilled } from "@tabler/icons-react";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

const ContentBox = ({ type, className }) => {
  const location = useLocation();
  const [categoryname, setCategoryname] = useState("");
  const [resources, setResources] = useState([]);
  const [instructions, setInstructions] = useState("");
  const [categoryTitle, setCategoryTitle] = useState("");
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);
  const [showResourcesModal, setShowResourcesModal] = useState(false);
  const BASE_URL =
    process.env.REACT_APP_BASE_URL || `${process.env.REACT_APP_BASE_URL}`;

  // Update categoryname based on the URL
  useEffect(() => {
    setCategoryname(location.pathname.split("/")[3]);
  }, [location]);

  // Update categoryTitle based on categoryname
  useEffect(() => {
    switch (categoryname) {
      case "cv_upload":
        setCategoryTitle("CV Upload");
        break;
      case "shortlisting_of_universities":
        setCategoryTitle("Shortlisting");
        break;
      case "sop":
        setCategoryTitle("SOP");
        break;
      case "lor":
        setCategoryTitle("LOR");
        break;
      case "ug_only":
        setCategoryTitle("Ug_Only");
        break;
      case "university_specific_sop":
        setCategoryTitle("University Specific SOP");
        break;
      case "final_documents_upload":
        setCategoryTitle("Final Documents Upload");
        break;

      default:
        setCategoryTitle("CV Upload");
        break;
    }
  }, [categoryname]);

  // Fetch instructions and resources based on categoryTitle
  useEffect(() => {
    if (!categoryTitle) return;

    const fetchInstructions = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/api/categories/${encodeURIComponent(categoryTitle)}/`
        );
        setInstructions(response.data.instructions || "");
      } catch (error) {
        console.error(
          "Failed to fetch instructions:",
          error.response?.data || error.message
        );
      }
    };

    const fetchResources = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/api/resources/${encodeURIComponent(categoryTitle)}/`
        );
        setResources(response.data);
      } catch (error) {
        console.error(
          "Failed to fetch resources:",
          error.response?.data || error.message
        );
      }
    };

    fetchInstructions();
    fetchResources();
  }, [categoryTitle]);

  const createMarkup = () => {
    return { __html: instructions };
  };

  // Modal components
  const InstructionsModal = () => (
    <div className="fixed inset-0 top-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[80vh] overflow-hidden">
        <div className="flex justify-between items-center border-b p-4">
          <h3 className="text-xl font-semibold text-gray-800">
            Instructions for {categoryTitle}
          </h3>
          <button
            onClick={() => setShowInstructionsModal(false)}
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-9rem)]">
          <div
            dangerouslySetInnerHTML={createMarkup()}
            className="text-gray-700 ql-editor"
          />
        </div>
        {/* <div className="border-t p-4 flex justify-end">
          <button
            onClick={() => setShowInstructionsModal(false)}
            className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            Close
          </button>
        </div> */}
      </div>
    </div>
  );

  const ResourcesModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[80vh] overflow-hidden">
        <div className="flex justify-between items-center border-b p-4">
          <h3 className="text-xl font-semibold text-gray-800">
            Resources for {categoryTitle}
          </h3>
          <button
            onClick={() => setShowResourcesModal(false)}
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-8rem)]">
          {Array.isArray(resources) && resources.length > 0 ? (
            <ul className="space-y-4">
              {resources.map((resource, index) => (
                <li key={index} className="border-b pb-3">
                  <a
                    href={`${process.env.REACT_APP_BASE_URL}/media/resources/${categoryTitle}/${resource.name}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:text-blue-700 font-medium flex items-center"
                  >
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                      />
                    </svg>
                    {resource.name}
                  </a>
                  {resource.description && (
                    <p className="text-gray-600 text-sm mt-1 ml-7">
                      {resource.description}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-center py-8">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p className="mt-2 text-gray-500">No resources available.</p>
            </div>
          )}
        </div>
        {/* <div className="border-t p-4 flex justify-end">
          <button
            onClick={() => setShowResourcesModal(false)}
            className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            Close
          </button>
        </div> */}
      </div>
    </div>
  );

  if (type === "instructions") {
    return (
      <div className={`w-full h-full ${className}`}>
        <div
          className="bg-green-50 border border-blue-200 rounded-lg"
          style={{ width: "100%" }}
        >
          <div className="p-3">
            <div className="flex items-center mb-1">
              <IconBulbFilled stroke={2} className="mr-2 text-green-700" />
              <span className="text-lg font-semibold text-green-800">
                Instructions
              </span>
            </div>
            <div
              dangerouslySetInnerHTML={createMarkup()}
              className="text-gray-700 overflow-y-auto "
              style={{
                height: "80px",
                scrollbarWidth: "thin",
                scrollbarColor: "#CBD5E0 transparent",
                overflowY: "auto",
              }}
            />
            <div className="mt-3 text-right">
              <button
                onClick={() => setShowInstructionsModal(true)}
                className="text-green-600 hover:text-green-800 text-sm font-medium focus:outline-none bg-white hover:bg-green-50 py-1 px-3 rounded-md border border-green-200 transition-colors inline-flex items-center"
              >
                <span>Read More</span>
                <svg
                  className="w-4 h-4 ml-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
        {showInstructionsModal && <InstructionsModal />}
      </div>
    );
  }

  if (type === "resources") {
    return (
      <div className={`w-full h-full ${className}`}>
        <div
          className="bg-white border border-blue-200 rounded-lg"
          style={{ width: "100%" }}
        >
          <div className="p-3">
            <div className="mb-1">
              <span className="text-lg font-semibold">Resources</span>
            </div>
            <div
              className="overflow-y-auto"
              style={{
                height: "50px",
                scrollbarWidth: "thin",
                scrollbarColor: "#CBD5E0 transparent",
                overflowY: "auto",
              }}
            >
              {Array.isArray(resources) && resources.length > 0 ? (
                <ul className="text-blue-500">
                  {resources.slice(0, 3).map((resource, index) => (
                    <li key={index}>
                      <a
                        href={`${process.env.REACT_APP_BASE_URL}/media/resources/${categoryTitle}/${resource.name}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:text-blue-700"
                      >
                        {resource.name}
                      </a>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500">No resources available.</p>
              )}
            </div>
            <div className="mt-3 text-right">
              <button
                onClick={() => setShowResourcesModal(true)}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium focus:outline-none bg-white hover:bg-blue-50 py-1 px-3 rounded-md border border-blue-200 transition-colors inline-flex items-center"
              >
                <span>View all resources</span>
                <svg
                  className="w-4 h-4 ml-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
        {showResourcesModal && <ResourcesModal />}
      </div>
    );
  }

  return null;
};

export default ContentBox;
